import React, { useEffect, useRef, useState} from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi, RCCTooltip } from "react-common-components";
import Accordion from "src/components/Accordion";
import CheckPermission from "src/components/CheckPermission";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { PermissionEnum } from "src/context/Auth/interface";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { css } from "styled-components";
import { v4 } from "uuid";
import { SWOTType,ISwotItem, IObjectiveItem} from "../../interface";
import swotLabels, { useIntensity, useTendencySO } from "../../SwotLabels";
import { IRefModalSWOTMapBounds } from "../SWOTItens/components/interface";
import ModalSWOTMapBounds from "../SWOTItens/components/MapBounds";
import { UlTable,SpanStyled } from "../SWOTItens/styles";
import { ISWOTObjective } from "./interface";

const SWOTObjective: React.FC<ISWOTObjective> = ({
	objective,
	strategy,
	swotId,
	onClose,
	canChange
}) => {
	const { translate } = useTranslation();
	const { addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	
	const [ loading,setLoading] = useState<boolean>(false);
	const [visible,setVisible] = useState<boolean>(false);
	const [ itens,setItens] = useState<Array<IObjectiveItem>>([]);
	const refModalSWOTMapBounds = useRef<IRefModalSWOTMapBounds>(null);

	const {importance,intensity,urgency,tendencySO,tendencyWT,swotSections} = swotLabels(translate);	

	const returnSwotProp = (prop:"title"|"color",swotSection:SWOTType) => {
		if(prop === "title")
			return swotSections.find((x) => x.type===swotSection)?.title;
		return swotSections.find((x) => x.type===swotSection)?.color;
	};

	const deleteBond = async(mes005Id:number) => {
		confirm(
			translate("Exclusão de vinculo"),
			`${translate("Deseja realmente excluir o vinculo dos itens")} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "SWOTStrategies/DeleteObjectiveBond",
					urlParameters:{mes005Id,mea003Id:objective.mea003Id}	
				});
				onClose();
				setLoading(false);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir vinculo"), addToast);
				setLoading(false);
			},
			true
		);
	};

	const tableHeader = (type: SWOTType) => (
		<RCCGrid item container xs={12} sm={6} 
			styleInline={css`
					background-color: ${returnSwotProp("color",type)};
					color:white; 
					font-weight:bold;
					padding:4px;
				`}>
			<span>
				{returnSwotProp("title",type)}
			</span>
		</RCCGrid>
	);

	const requestObjectiveItens = async() => {
		setLoading(true);
		try {
			const {data} = await RCCServicesApi.getRequest({
				method: "SWOTStrategies/ListObjectiveItens",
				urlParameters:{
					swotId,
					objectiveId:objective.mea003Id,
					strategyId:strategy.taa015Id
				}	
			});
			setItens(data);
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar itens SWOT"), addToast);
			setLoading(false);
		}
	};

	const returnPopOverBody = (item: ISwotItem) => (
		<RCCGrid container item className="popover-body">
			{item.mes002Identificador &&
				<RCCGrid container>
					<Title color="purple" bold>{translate("Código")}:</Title>
					<SpanStyled>{item.mes002Identificador}</SpanStyled>
				</RCCGrid>
			}
			<RCCGrid container>
				<Title color="purple" bold>{translate("Importância")}:</Title> 
				<SpanStyled>{importance[item.mes002Importancia-1].label}</SpanStyled>
			</RCCGrid>
			<RCCGrid container>
				<Title color="purple" bold>
					{useIntensity(item.mes002Secao) ? translate("Intensidade") : translate("Urgência") }:
				</Title>
				<SpanStyled>
					{useIntensity(item.mes002Secao) ?
						intensity[item.mes002IntensidadeUrg-1].label 
						: 
						urgency[item.mes002IntensidadeUrg-1].label }
				</SpanStyled>
			</RCCGrid>
			<RCCGrid container>
				<Title color="purple" bold>{translate("Tendência")}:</Title>
				<SpanStyled>
					{useTendencySO(item.mes002Secao) ? 
						tendencySO[item.mes002Tendencia-1].label :
						tendencyWT[item.mes002Tendencia-1].label 
					}
				</SpanStyled>
			</RCCGrid>
			<RCCGrid container>
				<Title color="purple" bold>{translate("Pontuação")}:</Title>
				<SpanStyled>
					{item.mes002Pontuacao}
				</SpanStyled>
			</RCCGrid>
		</RCCGrid>
	);

	const handleClickExpand = (expanded: boolean) => {
		setVisible(expanded);
	};

	useEffect(() => {
		if(!visible)
			return;

		requestObjectiveItens();
	}, [visible]);

	return (
		<RCCGrid container>
			{loading ?
				<Loader/>
				:
				<>
					<Accordion 
						expanded={visible}
						unmountOnExit={false}
						onChange={(_, expanded) => handleClickExpand(expanded)}
						header={
							<RCCGrid container item wrap="nowrap">
								{translate(objective.mea003Nome)}
								{objective.mea003Descricao && 
									<RCCPopover
										header={{title:translate("Descrição")}}
										body={{text:translate(objective.mea003Descricao)}}
									>
										<RCCIcon
											name="infoCircle"
											style={{marginLeft:"4px"}}
										/>
									</RCCPopover>
								}
							</RCCGrid>
						}>
						<RCCGrid container item>
							<UlTable >
								<li className="list-component-header always">
									{tableHeader(strategy.section1)}
									{tableHeader(strategy.section2)}
								</li>
								{itens.map((bond) =>
									<li className="list-component-child" key={v4() }>
										<RCCGrid item container
											xs={12}
											sm={6}
											styleInline={css`
											@media(max-width: 600px){
												color: ${returnSwotProp("color",strategy.section1)};
											}
										`}
										>
											<RCCGrid item container
												xs={10}
												alignItems="center"
											>
												<span>
													{bond.item1.dimensionText && 
													<span style={{color:"var(--grey)"}}>[{bond.item1.dimensionText}] </span>
													}
													{bond.item1.mes002Titulo}
												</span>
											</RCCGrid>
											<RCCGrid item container
												xs={2}
												alignItems="center"
											>
												<RCCPopover
													header={{title:translate("Detalhes")}}
													body={{custom:
														returnPopOverBody(bond.item1)
													}}
													color="default"
												>
													<RCCIcon
														name="infoCircle"
														style={{marginLeft:"4px"}}
													/>
												</RCCPopover>
											</RCCGrid>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={6}
											styleInline={css`
											@media(max-width: 600px){
												color: ${returnSwotProp("color",strategy.section2)};
											}
										`}
										>
											<RCCGrid item container
												xs={10}
												alignItems="center"
											>
												<span>
													{bond.item2.dimensionText && 
														<span style={{color:"var(--grey)"}}>[{bond.item2.dimensionText}] </span>
													}
													{bond.item2.mes002Titulo}
												</span>
											</RCCGrid>
											<RCCGrid item container
												xs={2}
												alignItems="center"
											>
												<RCCPopover
													header={{title:translate("Detalhes")}}
													body={{custom:
														returnPopOverBody(bond.item2)
													}}
													color="default"
												>
													<RCCIcon
														name="infoCircle"
														style={{marginLeft:"4px"}}
													/>
												</RCCPopover>
												{canChange && 
												<>
													{objective.mea003Id === 0 ?
														<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
															<RCCTooltip
																text={translate("Criar ligação")}
															>
																<RCCButton 
																	onClick={() => refModalSWOTMapBounds.current?.openSWOTBondsHierarchyObjectives(bond.mes005Id,onClose)}
																	color="transparent" 
																	icon={{name:"link",color:"grey"}} 
																/>
															</RCCTooltip>				
														</CheckPermission>		
														:
														<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
															<RCCTooltip
																text={translate("Excluir ligação")}
																color="red">
																<RCCButton
																	color="transparent"
																	onClick={() => deleteBond(bond.mes005Id)}
																	icon={{name:"trash",color:"var(--red-light)"}}
																/>
															</RCCTooltip>				
														</CheckPermission>
													
													}
												</>
												}
											</RCCGrid>
										</RCCGrid>
									</li>
								)}
							</UlTable>
						</RCCGrid>
					</Accordion>
					<ModalSWOTMapBounds ref={refModalSWOTMapBounds}/>
				</>
			}
		</RCCGrid>
	);
};

export default SWOTObjective;
